/**
 * Módulo principal de validación de formularios
 */

import { Validators } from './validators.js';
import { ErrorManager } from './errorManager.js';

export class FormValidator {
    constructor() {
        this.errorManager = new ErrorManager();
        this.validationRules = new Map();
    }

    /**
     * Valida un campo individual
     */
    validateField(form, fieldName, showErrors = true) {
        const formName = form.name;
        const fieldObj = form[fieldName];
        
        if (!fieldObj) return true;

        let isValid = true;
        let errorMessage = '';

        const i18n = (typeof ZFM_VARS !== 'undefined' && ZFM_VARS.i18n) ? ZFM_VARS.i18n : {};

        // Limpiar error previo
        if (showErrors) {
            this.errorManager.removeError(formName, fieldName);
        }
        
        // Validación de campos obligatorios
        if (this.isFieldMandatory(form, fieldName)) {
            if (fieldObj.nodeName) {
                if (!Validators.validateRequired(fieldObj)) {
                    isValid = false;
                    errorMessage = i18n.field_required || 'Este campo es obligatorio';
                }
            } else {
                // Grupo de campos (radio/checkbox)
                if (!Validators.validateGroup(fieldObj)) {
                    isValid = false;
                    errorMessage = i18n.select_option || 'Debe seleccionar una opción';
                }
            }
        }

        // Si el campo está vacío y no es obligatorio, no validar más
        if (isValid && fieldObj.nodeName && !fieldObj.value.trim()) {
            return true;
        }

        // Validaciones específicas por tipo
        if (isValid && fieldObj.nodeName) {
            const checkType = fieldObj.getAttribute("checktype");
            
            switch (checkType) {
                case "c2": // Número
                    if (!Validators.validateNumber(fieldObj)) {
                        isValid = false;
                        errorMessage = i18n.invalid_number || 'Debe introducir un número válido';
                    }
                    break;
                    
                case "c3": // Moneda
                    if (!Validators.validateCurrency(fieldObj) || !Validators.validateDecimalLength(fieldObj, 10)) {
                        isValid = false;
                        errorMessage = i18n.invalid_currency || 'Debe introducir un valor monetario válido';
                    }
                    break;
                    
                case "c4": // Fecha
                    if (!Validators.validateDateFormat(fieldObj)) {
                        isValid = false;
                        errorMessage = i18n.invalid_date || 'Debe introducir una fecha válida (dd-MMM-yyyy)';
                    }
                    break;
                    
                case "c5": // Email
                    if (!Validators.validateEmailID(fieldObj)) {
                        isValid = false;
                        errorMessage = i18n.invalid_email || 'Debe introducir un email válido';
                    }
                    break;
                    
                case "c6": // URL
                    if (!Validators.validateLiveUrl(fieldObj)) {
                        isValid = false;
                        errorMessage = i18n.invalid_url || 'Debe introducir una URL válida';
                    }
                    break;
                    
                case "c7": // Teléfono
                    if (!Validators.validatePhone(fieldObj)) {
                        isValid = false;
                        errorMessage = i18n.invalid_phone || 'Debe introducir un teléfono válido';
                    }
                    break;
            }

            // Validación de campos que deben coincidir
            if (isValid && !Validators.validateSameAs(fieldObj, form)) {
                isValid = false;
                errorMessage = i18n.fields_mismatch || 'Los campos no coinciden';
            }
        }

        // Mostrar error si es necesario
        if (!isValid && showErrors) {
            this.errorManager.addError(formName, fieldName, errorMessage);
        }

        return isValid;
    }

    /**
     * Valida todos los campos de un formulario
     */
    validateForm(form, showErrors = true) {
        const formName = form.name;
        let isFormValid = true;

        // Limpiar errores previos
        if (showErrors) {
            this.errorManager.clearFormErrors(formName);
        }
        
        // Obtener arrays de campos del formulario
        const mandatoryFields = this.getMandatoryFields(form);
        const validationFields = this.getValidationFields(form);

        // Combinar todos los campos únicos
        const allFields = new Set([...mandatoryFields, ...validationFields]);
        

        // Validar cada campo
        for (const fieldName of allFields) {
            const fieldValid = this.validateField(form, fieldName, showErrors);
            if (!fieldValid) {
                isFormValid = false;
            }
        }

        
        return isFormValid;
    }

    /**
     * Configura la validación en tiempo real para un formulario
     */
    setupRealtimeValidation(form) {
        const mandatoryFields = this.getMandatoryFields(form);
        const validationFields = this.getValidationFields(form);
        const allFields = new Set([...mandatoryFields, ...validationFields]);

        for (const fieldName of allFields) {
            const fieldObj = form[fieldName];
            if (!fieldObj) continue;

            if (fieldObj.nodeName) {
                // Campo individual
                this.setupFieldValidation(fieldObj, form, fieldName);
            } else {
                // Grupo de campos (radio/checkbox)
                for (let i = 0; i < fieldObj.length; i++) {
                    this.setupFieldValidation(fieldObj[i], form, fieldName);
                }
            }
        }
    }

    /**
     * Configura la validación para un campo específico
     */
    setupFieldValidation(fieldElement, form, fieldName) {
        // Validar al perder el foco
        fieldElement.addEventListener('blur', () => {
            this.validateField(form, fieldName, true);
        });

        // Para campos de texto, también validar mientras se escribe (con debounce)
        if (fieldElement.type === 'text' || fieldElement.type === 'email' || fieldElement.type === 'url' || fieldElement.tagName === 'TEXTAREA') {
            let timeout;
            fieldElement.addEventListener('input', () => {
                clearTimeout(timeout);
                timeout = setTimeout(() => {
                    this.validateField(form, fieldName, true);
                }, 500); // Validar 500ms después de dejar de escribir
            });
        }

        // Para checkboxes y radios, validar al cambiar
        if (fieldElement.type === 'checkbox' || fieldElement.type === 'radio') {
            fieldElement.addEventListener('change', () => {
                this.validateField(form, fieldName, true);
            });
        }
    }

    /**
     * Obtiene los campos obligatorios de un formulario
     */
    getMandatoryFields(form) {
        const formName = form.name;
        const varName = `vars_${formName}`;
        const formVars = window[varName];
        
        // console.log('Form name:', formName);
        // console.log('Variable name:', varName);
        // console.log('Window variables:', Object.keys(window).filter(key => key.startsWith('vars_')));
        // console.log('Form variables:', formVars);
        
        return formVars?.zf_MandArray || [];
    }

    /**
     * Obtiene los campos con validación de un formulario
     */
    getValidationFields(form) {
        const formName = form.name;
        const varName = `vars_${formName}`;
        const formVars = window[varName];
        return formVars?.zf_FieldArray || [];
    }

    /**
     * Verifica si un campo es obligatorio
     */
    isFieldMandatory(form, fieldName) {
        const mandatoryFields = this.getMandatoryFields(form);
        return mandatoryFields.includes(fieldName);
    }
}